import { useRequiredContext } from "@redotech/react-util/context";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import type { Context } from "react";

/**
 * A dynamic hook to call any method from any RPC client.
 * Accepts a client context as the first argument and retrieves the client from it.
 *
 * @param clientContext - The React context containing the RPC client
 * @param options - Configuration object containing:
 *   - method: The method name to call on the RPC client
 *   - args: Arguments to pass to the RPC client method
 *   - options: Optional react-query options
 *
 * @example
 * ```
 * import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
 *
 * // In your component
 * const { data, isLoading, error } = useRpcQuery(RedoMarketingRpcClientContext, {
 *   method: 'getTeamCampaigns',
 *   args: {
 *     includeStats: true,
 *     searchString: searchQuery,
 *     filters: { status: statusFilters }
 *   },
 *   options: {
 *     enabled: !!searchQuery, // Conditionally enable the query
 *     staleTime: 5 * 60 * 1000, // 5 minutes
 *   }
 * });
 * ```
 */
export function useRpcQuery<
  TClient,
  M extends RpcMethodKeys<TClient>,
  TError extends Error = Error,
>(
  clientContext: Context<TClient | undefined>,
  method: M,
  args: NoInfer<RpcMethodArgs<TClient, M>>,
  options?: RpcQueryOptions<TClient, M, TError>,
): ReturnType<
  typeof useQuery<RpcMethodData<TClient, M>, TError, RpcMethodData<TClient, M>>
>;
export function useRpcQuery<
  TClient,
  M extends RpcMethodKeys<TClient>,
  TError extends Error = Error,
>(
  clientContext: Context<TClient | undefined>,
  queryConfig: {
    method: M;
    args: NoInfer<RpcMethodArgs<TClient, M>>;
    options?: RpcQueryOptions<TClient, M, TError>;
  },
): ReturnType<
  typeof useQuery<RpcMethodData<TClient, M>, TError, RpcMethodData<TClient, M>>
>;
export function useRpcQuery<
  TClient,
  M extends RpcMethodKeys<TClient>,
  TError extends Error = Error,
>(
  clientContext: Context<TClient | undefined>,
  methodOrConfig:
    | M
    | {
        method: M;
        args: NoInfer<RpcMethodArgs<TClient, M>>;
        options?: RpcQueryOptions<TClient, M, TError>;
      },
  argsArg?: NoInfer<RpcMethodArgs<TClient, M>>,
  optionsArg?: RpcQueryOptions<TClient, M, TError>,
) {
  const method =
    typeof methodOrConfig === "string" ? methodOrConfig : methodOrConfig.method;
  const args =
    typeof methodOrConfig === "string" ? argsArg : methodOrConfig.args;
  const options =
    typeof methodOrConfig === "string" ? optionsArg : methodOrConfig.options;

  if (args === undefined) {
    throw new Error(`RPC query args are required for method: ${method}`);
  }

  // Get the client instance from context
  const clientInstance = useRequiredContext(clientContext);

  // Use provided queryKey or generate a default one
  // Use the context's displayName for better cache key uniqueness
  const clientName = clientContext.displayName;
  const queryKey = options?.queryKey ?? [clientName, method as string, args];

  return useQuery({
    queryKey,
    queryFn: async (): Promise<RpcMethodData<TClient, M>> => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      const clientMethod = clientInstance[method] as RpcMethod<TClient, M>;
      return await clientMethod(args);
    },
    ...options,
  });
}

type Callable = (input: any, options?: any) => Promise<any>;

type RpcMethodKeys<TClient> = {
  [K in keyof TClient]-?: TClient[K] extends Callable ? K : never;
}[keyof TClient] &
  string;

type RpcMethod<TClient, M extends RpcMethodKeys<TClient>> = Extract<
  TClient[M],
  Callable
>;

type RpcMethodArgs<TClient, M extends RpcMethodKeys<TClient>> = Parameters<
  RpcMethod<TClient, M>
>[0];
type RpcMethodData<TClient, M extends RpcMethodKeys<TClient>> = Awaited<
  ReturnType<RpcMethod<TClient, M>>
>;

type RpcQueryOptions<
  TClient,
  M extends RpcMethodKeys<TClient>,
  TError extends Error,
> = Omit<
  UseQueryOptions<RpcMethodData<TClient, M>, TError, RpcMethodData<TClient, M>>,
  "queryFn" | "queryKey"
> &
  Partial<
    Pick<
      UseQueryOptions<
        RpcMethodData<TClient, M>,
        TError,
        RpcMethodData<TClient, M>
      >,
      "queryKey"
    >
  >;

type NoInfer<T> = [T][T extends any ? 0 : never];
